import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { BRAND } from "@/i18n/translations";
import LanguageToggle from "@/components/sections/LanguageToggle";

export default function Navbar() {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "home", label: t.nav.home, href: "#top" },
    { id: "podcast", label: t.nav.podcast, href: "#podcast" },
    { id: "academy", label: t.nav.academy, href: "#membership" },
    { id: "about", label: t.nav.about, href: "#ecosystem" },
    { id: "contact", label: t.nav.contact, href: "#contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0A0A0A]/85 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-[72px] flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={BRAND.logoUrl}
            alt="RED FLAG"
            className="h-10 w-10 object-contain"
          />
          <span className="font-display text-2xl tracking-wide uppercase hidden sm:inline">
            <span className="text-redflag">Red</span>{" "}
            <span className="text-white">Flag</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9 text-sm">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={`nav-link-${l.id}`}
              className="nav-link font-medium"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <LanguageToggle />
          <a
            href="#membership"
            data-testid="nav-cta-button"
            className="btn-primary inline-flex items-center px-5 py-2.5 text-sm font-medium uppercase tracking-wide"
          >
            {t.nav.cta}
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="md:hidden p-2 text-white"
          onClick={() => setOpen((s) => !s)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-white/5 bg-[#0A0A0A]/95 backdrop-blur-xl"
        >
          <div className="px-5 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.id}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.id}`}
                className="text-lg font-medium text-white/85"
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center justify-between pt-3 border-t border-white/10">
              <LanguageToggle />
              <a
                href="#membership"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-cta-button"
                className="btn-primary inline-flex px-5 py-2.5 text-sm font-medium uppercase"
              >
                {t.nav.cta}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
